import { useEffect, useMemo } from 'react';
import { useAppStateContext } from '@/shared';
import { useNavigate } from 'react-router';
import { useImmer } from 'use-immer';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import {
  getBranchListApi,
  getDeviceListByBranchIdApi,
  getSettingsApi,
  validateBranchAccessApi,
} from '../apis';
import { useUtilFunctions } from '@/utils';
import { deviceInfoWithAppVersion, getGeneralPath } from '@/utils/functions';

export const usePageDeviceConfig = () => {
  const navigate = useNavigate();
  const { appState, setAppState } = useAppStateContext();

  const [state, setState] = useImmer({
    isVerified: false,
    deviceList: [],
    storeList: [],
    isSubmitButtonLoading: false,
    logoUrl: null,
  });

  const { registerDevice, isDeviceDetailsFetching } = useUtilFunctions();

  const storeSchema = z.object({
    label: z.string(),
    value: z.number(),
    address: z.string().optional(),
    city: z.string().optional(),
    province: z.string().optional(),
    phone: z.string().optional(),
    emailAddress: z.string().nullable(),
    timeZone: z.string(),
    accessCode: z.string().optional(),
    deviceCount: z.number(),
  });

  const deviceSchema = z.object({
    label: z.string(),
    value: z.number(),
  });

  const FormSchema = z.object({
    store: storeSchema,
    accessCode: z
      .string()
      .trim()
      .min(1, { message: 'Access code is required.' }),
    device: state.isVerified ? deviceSchema : deviceSchema.optional(),
  });

  const form = useForm({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      store: undefined,
      accessCode: '',
    },
  });

  // =================================================================================== API SECTION =====================================================================================

  const getBranchList = async () => {
    try {
      const response = await getBranchListApi();
      const { data } = response;
      if (response.status === 200) {
        setState((draft) => {
          draft.storeList = data.map(({ name, branchId, ...props }) => ({
            label: name,
            value: branchId,
            ...props,
          }));
        });
      } else {
        throw response;
      }
    } catch (error) {
      const message = error?.response?.data?.message;
      form.setError('device', { message });
    }
  };

  const getSettings = async () => {
    try {
      const response = await getSettingsApi();
      const { data } = response;
      if (response.status === 200) {
        const authDetails = {
          ...appState,
          localData: {
            ...appState.localData,
            config: { ...appState.localData.config, ...data },
          },
        };
        setAppState(authDetails);
      } else {
        throw response;
      }
    } catch (error) {
      const message = error?.response?.data?.message;
      form.setError('device', { message });
    }
  };

  const getDeviceListByBranchId = async ({ branchId }) => {
    try {
      const response = await getDeviceListByBranchIdApi({ branchId });
      const { data } = response;
      if (response.status === 200) {
        const formattedData = data.map(
          ({ deviceId, name, accessToken, ...props }) => ({
            value: deviceId,
            label: `${name} (${accessToken ? 'Registered' : 'Available'})`,
            ...props,
          }),
        );

        setState((draft) => {
          draft.deviceList = formattedData;
        });
      } else {
        throw response;
      }
    } catch (error) {
      const message = error.response.data.message;
      form.setError('device', { message });
    }
  };

  const validateBranchAccess = async ({ branchId, accessCode }) => {
    triggerSubmitButtonLoading(true);
    try {
      const response = await validateBranchAccessApi({ branchId, accessCode });
      const { data } = response;
      if (response.status === 200) {
        if (data) {
          await getDeviceListByBranchId({ branchId });
          setState((draft) => {
            draft.isVerified = true;
          });
        } else {
          form.setError('accessCode', {
            message: 'Invalid access code',
          });
        }
      } else {
        throw response;
      }
    } catch (error) {
      const message = error.response.data?.message;
      form.setError('device', { message });
    } finally {
      triggerSubmitButtonLoading(false);
    }
  };

  // ================================================================================ UTIL FUNCTION SECTION =============================================================================

  const triggerSubmitButtonLoading = (loading) => {
    setState((draft) => {
      draft.isSubmitButtonLoading = loading;
    });
  };

  const onSubmit = async (data) => {
    if (!state.isVerified && !data?.device) {
      await validateBranchAccess({
        branchId: data.store.value,
        accessCode: data.accessCode,
      });
    } else if (state.isVerified && data?.device?.value) {
      await registerDevice({
        deviceId: data.device?.value,
        deviceInfo: deviceInfoWithAppVersion(),
      });
    }
  };

  const onResetFormData = () => {
    form.reset();
    setState((draft) => {
      return { ...draft, isVerified: false };
    });
    navigate('/reset');
  };

  // ================================================================================= USE EFFECT SECTION ===============================================================================
  useEffect(() => {
    getSettings();
    getBranchList();
  }, []);

  useMemo(() => {
    const logoUrl = appState?.localData?.config?.logo;

    if (logoUrl && appState.tid) {
      setState((draft) => {
        draft.logoUrl = getGeneralPath(appState.tid) + logoUrl;
      });
    }
  }, [appState?.localData?.config?.logo]);

  return { form, state, isDeviceDetailsFetching, onSubmit, onResetFormData };
};
